// Content for Bhagavad-Gītā 18.66 commentary topic

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Bhagavad-Gītā 18.66',
        sloka: `sarva-dharmān parityajya
mām ekaṁ śaraṇaṁ vraja
ahaṁ tvāṁ sarva-pāpebhyo
mokṣayiṣyāmi mā śucaḥ`,
        translation:
            'Giving up all dharmas (<strong>sarva-dharmān parityajya</strong>), just surrender unto me alone (<strong>mām ekaṁ śaraṇaṁ vraja</strong>). I will deliver you from all sins (<strong>ahaṁ tvāṁ sarva-pāpebhyo mokṣayiṣyāmi</strong>). Do not worry (<strong>mā śucaḥ</strong>).',
    },
    blocks: [
        {
            type: 'dialogue',
            objection: '"Am I supposed to perform the meditation and other processes along with my varṇa āśrama duties or without them?"',
            response: '"Giving up all duties of varṇa and āśrama (sarva-dharmān parityajya), surrender only to Me."',
        },
        {
            type: 'text',
            content:
                'One should not say that <em>parityaja</em> means <em>sannyasya</em>, to adopt the sannyāsa order, because Arjuna was a kṣatriya, not qualified for sannyāsa.<br /><br />As well it should not be said the Lord used Arjuna just to instruct all other people who are not kṣatriyas to take sannyāsa, for Arjuna was qualified to be the recipient of the Lord\'s instructions which could not be taught to others.<br /><br /><strong>Nor should one explain the meaning of <em>parityajya</em> in this verse as merely "give up all the results of activities."</strong>',
        },
        {
            type: 'text',
            content: 'For it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.5.41',
                sloka: `devarṣi-bhūtāpta-nṛṇāṁ pitṛṇāṁ
na kiṅkaro nāyam ṛṇī ca rājan
sarvātmanā yaḥ śaraṇaṁ śaraṇyaṁ
gato mukundaṁ parihṛtya kartam`,
                translation:
                    'Anyone who has taken shelter of the lotus feet of Mukunda, the giver of liberation, giving up all kinds of obligation, and has taken to the path in all seriousness, owes neither duties nor obligations to the demigods, sages, general living entities, family members, humankind or forefathers.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.29.35',
                sloka: `martyo yadā tyakta-samasta-karmā
niveditātmā vicikīrṣito me
tadāmṛtatvaṁ pratipadyamāno
mayātma-bhūyāya ca kalpate vai`,
                translation:
                    'A person who gives up all fruitive activities and offers himself entirely unto Me, eagerly desiring to render service unto Me, achieves liberation from birth and death and is promoted to the status of sharing My own opulences.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.20.9',
                sloka: `tāvat karmāṇi kurvīta
na nirvidyeta yāvatā
mat-kathā-śravaṇādau vā
śraddhā yāvan na jāyate`,
                translation:
                    'As long as one is not satiated by fruitive activity and has not awakened his taste for devotional service by śravaṇaṁ kīrtanaṁ viṣṇoḥ [SB 7.5.23] one has to act according to the regulative principles of the Vedic injunctions.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.11.37',
                sloka: `ājñāyaivaṁ guṇān doṣān
mayādiṣṭān api svakān
dharmān santyajya yaḥ sarvān
māṁ bhajeta sa tu sattamaḥ`,
                translation:
                    'He perfectly understands that the ordinary religious duties prescribed by Me in various Vedic scriptures possess favorable qualities that purify the performer, and he knows that neglect of such duties constitutes a discrepancy in one\'s life. Having taken complete shelter at My lotus feet, however, a saintly person ultimately renounces such ordinary religious duties and worships Me alone. He is thus considered to be the best among all living entities.',
            },
        },
        {
            type: 'text',
            content:
                'The meaning of the word should thus be explained using all these statements of the Lord with no contradiction. The meaning can indeed be understood correctly by the prefix <em>pari</em> which means "completely."',
        },
        {
            type: 'text',
            content:
                '<strong>"Surrender only to Me (mām ekaṁ śaraṇaṁ vraja)." This means that there should be no worship of devatās, aṣṭāṅga-yoga, jñāna or dharma or other elements in that surrender.</strong>',
        },
        {
            type: 'text',
            content:
                '<strong>"Previously I have said that you were not qualified for ananyā bhakti, the highest type of bhakti, and that you were qualified for karma-miśra-bhakti, when I said \'Whatever you do, whatever you eat, do it as an offering to Me.\' But now, by My great mercy, you have qualification for ananyā bhakti. That ananyā bhakti can only be attained by the causeless mercy of My ekāntika-bhakta. Though that is a rule I myself have made, I am now giving the mercy Myself, breaking My own vow, as I did in the fight with Bhīṣma.</strong>',
        },
        {
            type: 'text',
            content:
                '<strong>"And by following My orders you should not fear some loss on your part in giving up nitya and naimittika-karmas. The order to perform these nitya-karmas was given by Me in the form of the Vedas. Now, I am ordering you to give them all up completely.</strong>"',
        },
        {
            type: 'dialogue',
            objection: '<strong>"Is there sin in not performing your nitya-karmas?"</strong>',
            response:
                '<strong>"No, rather the opposite—in performing nitya-karmas you will commit sin, because of disobeying My direct order."</strong>',
        },
        {
            type: 'dialogue',
            objection:
                '"If someone surrenders to You, he becomes dependent, like an animal purchased for a price. He does whatever You make him do. If You put him in one place, he stays there. If You offer him food, he eats. These are the characteristics of surrender. But the Vāyu Purāṇa says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Vāyu Purāṇa',
                sloka: `ānukūlyasya saṅkalpaḥ
pratikūlyasya varjanaṁ
rakṣiṣyatīti viśvāso
goptṛtve varaṇaṁ tathā
ātma-nikṣepa-kārpaṇye
ṣaḍ-vidhā śaraṇāgatiḥ`,
                translation:
                    'Ānukūlya means conduct which is pleasing to the Lord and according to the scriptures. Pratikūlya means the opposite. Goptṛtva means to think of the Lord and no one else as ones protector. One should have faith (viśvasaḥ) that in times when ones own existence is threatened, the Lord will give protection, as in the case of Gajendra or Draupadī. Niḥkṣepaṇam means employment of ones gross and subtle bodies for Kṛṣṇa\'s service. Akārpaṇya means that one should not make a show of ones own humility. Surrender (śaraṇa-gati) consists of performance of these six items in relation to the Lord.',
            },
        },
        {
            type: 'dialogue',
            showObjectionLabel: false,
            objection:
                '"Then starting today, if I surrender to You, I should then do whatever You say whether it is good or bad. If You make me perform dharma, then I will not worry at all. But if You engage me in adharma, since You are the Supreme Lord and can do what You want, then what will happen to me? Please tell me."',
            response:
                '"I will free you from all sinful reactions—from whatever reactions exist from the far past and recent past, and from whatever reactions arise from acts I will make you perform in the future. This is not impossible for Me to do, though it cannot be done by anyone else to whom you surrender.<br /><br />Taking you as the means, I am giving instructions to the whole world. Do not feel grief for your own welfare or that of others. May you and all other people, giving up all dharmas—your own and everyone else\'s—absorbing your thoughts and actions in Me, surrendering to Me, remain in contentment.<br /><br /><strong>I Myself have accepted the burden of freeing you from sin, and freeing you from saṁsāra.</strong>"',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-Gītā 9.22',
                sloka: `ananyāś cintayanto māṁ
ye janāḥ paryupāsate
teṣāṁ nityābhiyuktānāṁ
yoga-kṣemaṁ vahāmy aham`,
                translation:
                    'But I carry the burden of supply and maintenance of those who desire constant association with Me, and who, thinking only of Me, worship only Me.',
            },
        },
        {
            type: 'text',
            content:
                'Do not lament thinking \'Oh, I have thrown my own burden on my master!\' <strong>It is no strain at all for Me, who am most affectionate to My devotee.</strong> Nothing else remains to be instructed."',
        },
        {
            type: 'text',
            content: 'Thus the scripture has been concluded.',
        },
    ],
};

export default content;
